// src/features/settings/bankaccounts/services/BankAccountService.ts
import axiosInstance from '../../../../config/axios';

export interface BankAccount {
  id: number;
  name: string;
  holder_name: string;       // ← BE-konform
  iban_masked: string;       // kommt nur in Responses
  bic?: string | null;
  bank_name?: string | null;
  country_code?: string | null;
  currency: string;          // 'EUR'
  is_payroll_default: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateBankAccountPayload {
  name: string;
  holder_name: string;       // ← NICHT account_holder
  iban: string;              // unmaskiert; wird im BE verschlüsselt
  bic?: string | null;
  bank_name?: string | null;
  currency?: string;         // default 'EUR'
  is_payroll_default?: boolean;
}

export interface UpdateBankAccountPayload {
  name?: string;
  holder_name?: string;
  iban?: string;            // optional: nur wenn neue IBAN eingegeben wurde
  bic?: string | null;
  bank_name?: string | null;
  currency?: string;
  is_payroll_default?: boolean;
  active?: boolean;
}

const BASE = '/api/payments/bank-accounts/';

// Erlaubte Felder für PATCH-Requests (Whitelist)
const PATCH_ALLOWED = [
  'name',
  'holder_name',
  'iban',                // nur wenn neue IBAN angegeben wurde!
  'bic',
  'bank_name',
  'currency',
  'is_payroll_default',
  'active',
];

// Helfer: Results-Wrapper tolerant verarbeiten
function unwrap<T>(data: any): T[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function buildPatchBody(payload: Record<string, any>): Record<string, any> {
  const entries = Object.entries(payload).filter(
    ([k, v]) => PATCH_ALLOWED.includes(k) && v !== undefined
  );
  return Object.fromEntries(entries);
}

export default class BankAccountService {
  static async getBankAccounts(): Promise<BankAccount[]> {
    const { data } = await axiosInstance.get(BASE);
    return unwrap<BankAccount>(data);
  }

  static async getBankAccount(id: number): Promise<BankAccount> {
    const { data } = await axiosInstance.get(`${BASE}${id}/`);
    return data as BankAccount;
  }

  static async createBankAccount(payload: CreateBankAccountPayload): Promise<BankAccount> {
    const normalized: CreateBankAccountPayload = {
      ...payload,
      iban: (payload.iban || '').replace(/\s|-/g, '').toUpperCase(),
      currency: payload.currency || 'EUR',
    };
    const { data } = await axiosInstance.post(BASE, normalized);
    return data as BankAccount;
  }

  static async updateBankAccount(id: number, payload: UpdateBankAccountPayload): Promise<BankAccount> {
    const body = buildPatchBody(payload);

    // Normalisiere IBAN wenn vorhanden
    if (body.iban && typeof body.iban === 'string') {
      body.iban = body.iban.replace(/\s|-/g, '').toUpperCase();
    }

    const { data } = await axiosInstance.patch(`${BASE}${id}/`, body);
    return data as BankAccount;
  }

  static async deleteBankAccount(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}${id}/`);
  }

  static async setAsDefault(id: number): Promise<void> {
    // BE-Spezial-Endpoint, wie besprochen
    await axiosInstance.post(`${BASE}${id}/make-default/`, {});
  }
}
